<template>
  <v-app class="app">
    <div class="nav-icon-collapsed">
      <v-btn v-if="show_drawer" variant="text" :icon="drawer ? 'mdi-chevron-left' : 'mdi-chevron-right'"
        @click.stop="switch_drawer">
      </v-btn>
    </div>
    <v-navigation-drawer permanent v-if="show_drawer" :rail="!drawer" touchless app width="400">
      <div class="my-13"></div>
      <NavigationMenu :rail="!drawer" />
    </v-navigation-drawer>
    <v-main>
      <router-view></router-view>
      <Notifications />
    </v-main>
    <v-overlay :model-value="loading_overlay" class="d-flex align-center justify-center">
      <span class="text-accent text-h6"> Loading ... </span>
    </v-overlay>

  </v-app>
</template>

<style lang="scss">
// set left padding for <ul> and <li> elements
// in all texts in the app
ul {
  padding-left: 20px;
}


.app {
  height: 100hv;
  touch-action: none;
}

.nav-icon-collapsed {
  position: fixed;
  top: 15px;
  left: 5px;
  z-index: 10000;
}

::-webkit-scrollbar {
  width: 12px;
  /* width of the entire scrollbar */
  height: 12px;
}

::-webkit-scrollbar-thumb {
  background-color: #ff770054;
  /* color of the scroll thumb */
  border-radius: 12px;
  /* roundness of the scroll thumb */
}
</style>

<script>
import NavigationMenu from "./components/NavigationMenu.vue";
import { useAppStore } from "./stores/app";
import { useSessionStore } from "./stores/session";
import Notifications from "/src/components/Notifications";
import bus from "/src/utils/event_bus";


export default {
  components: {
    NavigationMenu,
    Notifications,
  },
  data: () => ({
    drawer: false,
    userCheckInterval: null,
    lastActivity: Date.now(),
    consecutiveFailures: 0,
    maxConsecutiveFailures: 3,
    isUserActive: true,
    activityCheckInterval: null,
  }),
  computed: {
    loading_overlay() {
      const app_store = useAppStore();
      return app_store.loader_is_active("base_user_data") || app_store.loader_is_active("route");
    },
    show_drawer() {
      return !["login"].includes(this.$route.name);
    },
  },
  watch: {
  },
  methods: {
    switch_drawer() {
      this.drawer = !this.drawer;
    },
    trackUserActivity() {
      this.lastActivity = Date.now();
      if (!this.isUserActive) {
        this.isUserActive = true;
        console.log("[Activity] User is active again, resuming periodic checks");
      }
    },

    checkUserActivity() {
      const inactiveThreshold = 5 * 60 * 1000; // 5 minutes
      const timeSinceLastActivity = Date.now() - this.lastActivity;

      if (timeSinceLastActivity > inactiveThreshold && this.isUserActive) {
        this.isUserActive = false;
        console.log("[Activity] User inactive, pausing periodic checks");
      }
    },

    async performPeriodicUserCheck() {
      // Skip if user is inactive or we've had too many consecutive failures
      if (!this.isUserActive || this.consecutiveFailures >= this.maxConsecutiveFailures) {
        return;
      }

      const app_store = useAppStore();
      const session_store = useSessionStore();

      // Skip if not authenticated
      if (!session_store.authenticated) {
        return;
      }

      try {
        await app_store.refreshBaseUserData(false);
        // Reset failure counter on success
        this.consecutiveFailures = 0;
      } catch (error) {
        this.consecutiveFailures++;
        console.warn(`[Auth] Periodic check failed (${this.consecutiveFailures}/${this.maxConsecutiveFailures})`, error);

        // If we've reached max failures, stop all periodic checks
        if (this.consecutiveFailures >= this.maxConsecutiveFailures) {
          console.error("[Auth] Max consecutive failures reached, stopping periodic checks");
          this.stopPeriodicChecks();

          // Check if it's a 401 error (session expired)
          if (error.response?.status === 401) {
            bus.emit('notification', {
              timeout: 8000,
              text: "Session expired. Please log in again.",
              color: "error"
            });
            bus.emit("events/session/logout");
          }
        }
      }
    },

    startPeriodicChecks() {
      // Clear any existing interval
      this.stopPeriodicChecks();

      // Start user data refresh every 2 minutes
      this.userCheckInterval = setInterval(() => {
        this.performPeriodicUserCheck();
      }, 120000);

      // Start activity monitoring every 30 seconds
      this.activityCheckInterval = setInterval(() => {
        this.checkUserActivity();
      }, 30000);

      console.log("[Auth] Started periodic checks with activity detection");
    },

    stopPeriodicChecks() {
      if (this.userCheckInterval) {
        clearInterval(this.userCheckInterval);
        this.userCheckInterval = null;
      }
      if (this.activityCheckInterval) {
        clearInterval(this.activityCheckInterval);
        this.activityCheckInterval = null;
      }
      console.log("[Auth] Stopped periodic checks");
    },

    setupActivityListeners() {
      // Track user activity
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

      activityEvents.forEach(event => {
        document.addEventListener(event, this.trackUserActivity, { passive: true });
      });
    },

    removeActivityListeners() {
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

      activityEvents.forEach(event => {
        document.removeEventListener(event, this.trackUserActivity);
      });
    }
  },
  mounted() {
    const session_store = useSessionStore();
    const app_store = useAppStore();

    // EMERGENCY FIX: Add protection against loops and only run on authenticated routes
    if (this.$route.name === 'login') {
      console.log("[Auth] On login page, skipping auth initialization");
      return;
    }

    // Setup activity tracking
    this.setupActivityListeners();

    // Listen for logout events to stop periodic checks FIRST
    bus.on("events/session/logout", () => {
      console.log("[Auth] Logout event received, stopping all checks");
      this.stopPeriodicChecks();
    });

    // EMERGENCY FIX: Only proceed if we have tokens
    if (!session_store.authenticated || !session_store.access_token) {
      console.log("[Auth] No valid tokens found, redirecting to login");
      this.$router.push('/login').catch(() => { });
      return;
    }

    // EMERGENCY FIX: Add delay and error handling for initial calls
    setTimeout(async () => {
      try {
        console.log("[Auth] Starting initial authentication sequence");

        // Try refresh first, but don't fail if it doesn't work
        try {
          await session_store.refresh();
          console.log("[Auth] Token refresh successful");
        } catch (refreshError) {
          console.warn("[Auth] Token refresh failed, but continuing:", refreshError.message);
        }

        // Try to refresh user data FIRST (para que user_object.auth esté disponible)
        try {
          await app_store.refreshBaseUserData(false);
          console.log("[Auth] User data refresh successful");
        } catch (userError) {
          console.warn("[Auth] User data refresh failed:", userError.message);
        }

        // Try to load initial data AFTER (ahora user_object.auth está disponible)
        // Pasar si es externo como parámetro explícito
        try {
          const isExternal = session_store.is_external_customer || session_store.is_external_workshop;
          await app_store.initialLoad(isExternal);
          console.log("[Auth] Initial data load successful");
        } catch (loadError) {
          console.warn("[Auth] Initial load failed:", loadError.message);
        }

        // Only start periodic checks if everything went well
        console.log("[Auth] Starting periodic checks");
        this.startPeriodicChecks();

      } catch (error) {
        console.error("[Auth] Critical error during initialization:", error);
        // Don't trigger logout here, just log the error
      }
    }, 1000); // 1 second delay to avoid race conditions
  },

  beforeUnmount() {
    this.stopPeriodicChecks();
    this.removeActivityListeners();
  }
};
</script>
